import React, { useState, memo } from 'react';
import useCars from 'app/hook/cars/useCars';
import useDetailPage from 'app/hook/cars/useDetailPage';
import useService from 'app/hook/cars/useService';
import useOption from 'app/hook/cars/useOption';
import useLayer from 'app/hook/common/useLayer';
import usePage from 'app/hook/common/usePage';

import { BottomSheet, Button } from '@encarpkg/design';
import { Enlog } from '@encarpkg/shared';
import LayerChoiceOption from 'app/components/cars/LayerChoiceOption';
import ResponsiveTemplate from 'app/components/cars/ResponsiveTemplate';
import MajorOptionList from 'app/components/common/MajorOptionList';

import classNames from 'classnames/bind';
import styles from './DetailOption.module.scss';

const cx = classNames.bind(styles);

function DetailOption() {
	const { base, sellingpoint } = useCars();
	const { viewMode } = useDetailPage();
	const { isService } = useService();
	const { baseChoice, choiceData, optionActiveTotal } = useOption();
	const { handlePageOpenAuto } = usePage();
	const { view: optionView, handleLayerOpen: handleOptionLayerOpen, handleLayerClose: handleOptionLayerClose } = useLayer();
	const isPc = viewMode === 'pcMode';

	const handleChoiceImage = () => {
		handleOptionLayerOpen();
	};

	const [viewUniqueOptionLayer, setViewUniqueOptionLayer] = useState(false);
	const [layerData, setLayerData] = useState({
		title: '',
		content: '',
	});
	const handleViewUniqueOptionLayerOpen = (title, content) => {
		setLayerData({
			title,
			content,
		});
		setViewUniqueOptionLayer(true);
		const dataTalkViewSingle = {
			eventNameGroup: '이차만의옵션',
		};
		Enlog().enloggingJsonData({
			dataTalkViewSingle,
		});
	};
	const handleViewUniqueOptionLayerClose = () => {
		setViewUniqueOptionLayer(false);
	};

	const handleOptionMore = () => {
		if (isPc) {
			window.open(`/cars/option/${base.vehicleId}`, 'optionMycar', 'width=500,height=920,scrollbars=yes');
		} else {
			handlePageOpenAuto('pageOption', `/cars/option/${base.vehicleId}`);
		}
	};
	const handleOptionDesc = (e) => {
		e.preventDefault();
		window.open(`/cars/option-info?carType=${base.vehicleType.toLowerCase()}`, 'optionDesc', 'width=810,height=1022,scrollbars=yes');
	};

	// 차량 기본 옵션 코드 배열
	const standardOptions = base.options?.standard || [];

	const choiceOptionDomImage = sellingpoint.choiceOptionPhotos
		?.filter((n) => n.attachment)
		.map((item) => (
			<div
				key={item.attachment.attachmentId}
				className={cx('bundle')}
			>
				<button
					type="button"
					onClick={handleChoiceImage}
					disabled={isPc}
					className={cx('btn')}
					data-enlog-dt-eventname="선택옵션이미지"
				>
					<img
						src={`${item.attachment.url[0].replace('http://imgcar', 'https://imgcar')}?d=640x360`}
						className={cx('thumb')}
						alt="선택옵션이미지"
					/>
				</button>
			</div>
		));

	const choiceOptionDom = choiceData
		.sort((a, b) => a.price - b.price)
		.map((item) => (
			<li key={item.optionCd}>
				<em className={cx('tit')}>
					{item.optionName}
					{item.description && (
						<button
							type="button"
							className={cx('btn_more')}
							onClick={() => handleViewUniqueOptionLayerOpen(item.optionName, item.description)}
						>
							선택옵션설명 자세히보기
						</button>
					)}
				</em>
				<span
					className={cx('txt')}
					data-intl-currency
				>
					<span data-intl-currency-amount={item.price * 10000}>{item.price}</span>
					<span data-intl-currency-unit>만원</span>
				</span>
			</li>
		));

	return (
		<>
			<div data-impression="주요옵션">
				<div className={cx('tit_area')}>
					<strong className={cx('tit_option')}>주요옵션</strong>
					{isPc && (
						<a
							href={`/cars/option-info?carType=${base.vehicleType.toLowerCase()}`}
							className={cx('btn_option')}
							onClick={(e) => handleOptionDesc(e)}
							target="_blank"
							rel="noreferrer"
							data-enlog-dt-eventnamegroup="옵션정보"
						>
							옵션 설명 보기
						</a>
					)}
				</div>
				{/* 차량상세 주요옵션 리스트 영역 */}
				<MajorOptionList
					vehicleType={base.vehicleType}
					standardOptions={standardOptions}
				/>

				<ResponsiveTemplate.ButtonType>
					<Button.V2
						domType="button"
						color="black_outlined"
						size={44}
						data-enlog-dt-eventname="옵션모두보기"
						data-enlog-dt-eventnamegroup="옵션정보"
						onClick={handleOptionMore}
					>
						{optionActiveTotal}개 옵션 모두보기
					</Button.V2>
				</ResponsiveTemplate.ButtonType>
			</div>

			{baseChoice.length > 0 && (
				<ResponsiveTemplate.TextImageType
					area="wrap"
					data-impression="이차만의옵션"
				>
					<ResponsiveTemplate.TextImageType area="text">
						<strong className={cx('tit_option')}>
							이 차만의 옵션
							<button
								type="button"
								className={cx('btn_more')}
								data-enlog-dt-eventname="이차만의옵션"
								data-enlog-dt-eventnamegroup="옵션정보"
								onClick={() =>
									handleViewUniqueOptionLayerOpen(
										'이 차만의 옵션',
										'신차 계약 당시 차량 기본가격 외 제조사에서 제공하는 선택 옵션을 추가 비용을 들여 장착한 옵션입니다.',
									)
								}
							>
								더보기
							</button>
						</strong>
						<ul className={cx('choose_option')}>{choiceOptionDom}</ul>
					</ResponsiveTemplate.TextImageType>
					{isService.warranty && choiceOptionDomImage && choiceOptionDomImage.length > 0 && (
						<ResponsiveTemplate.TextImageType area="image">
							<div className={cx('choose_option_image', { only: choiceOptionDomImage.length === 1 })}>{choiceOptionDomImage}</div>
						</ResponsiveTemplate.TextImageType>
					)}
				</ResponsiveTemplate.TextImageType>
			)}

			{optionView && <LayerChoiceOption handleLayerClose={handleOptionLayerClose} />}

			<BottomSheet
				isOpen={viewUniqueOptionLayer}
				dimClosable
				header={layerData.title}
				onClose={() => handleViewUniqueOptionLayerClose()}
			>
				<div className={cx('info_unique')}>
					<p className={cx('desc_unique')}>{layerData.content}</p>
					<Button.V2
						domType="button"
						color="black_outlined"
						size={44}
						onClick={() => handleViewUniqueOptionLayerClose()}
						className={cx('btn_close')}
					>
						확인
					</Button.V2>
				</div>
			</BottomSheet>
		</>
	);
}

export default memo(DetailOption);
